import * as React from 'react'
import {observer} from 'mobx-react-lite'
import {Tab, TabProps as MuiTabProps, Tabs as MuiTabs, TabsProps as MuiTabsProps, Tooltip} from '@mui/material'
import {DataCyProps} from './dataCyProps'
import {syskronPalette} from '@oc/styles'
import {useQueryParameterState} from '@oc/hooks'

export interface TabInfo {
    header: {
        'data-cy'?: string
        label: string
        error?: boolean
        additionalProps?: MuiTabProps & DataCyProps & {tooltip?: string}
    }
    content: React.ReactNode
}

export interface TabsProps {
    tabs: TabInfo[]
    selectedTab: number
    setSelectedTab: (newValue: number) => void
    rootClassName?: string
    tabRootClassName?: string
    tabsProps?: Omit<MuiTabsProps, 'value' | 'onChange'>
    fallbackTabContent?: React.ReactNode
}

function _Tabs(props: TabsProps) {
    const {tabs, selectedTab, setSelectedTab, rootClassName, tabRootClassName, tabsProps, fallbackTabContent} = props

    const handleChange = React.useCallback(
        (_: React.ChangeEvent<unknown>, newValue: number) => {
            setSelectedTab(newValue)
        },
        [setSelectedTab]
    )

    const dataArea = React.useMemo(() => {
        const selected = 0 <= selectedTab && selectedTab < tabs.length ? tabs.at(selectedTab) : undefined
        return (
            <div role="tabpanel" className={tabRootClassName} key={`${selected?.header.label || 'unknown'}-content`}>
                {selected?.content || fallbackTabContent}
            </div>
        )
    }, [tabs, selectedTab, tabRootClassName, fallbackTabContent])

    return (
        <div className={rootClassName}>
            <MuiTabs
                textColor="primary"
                {...tabsProps}
                value={0 <= selectedTab && selectedTab < tabs.length ? selectedTab : false}
                onChange={handleChange}
            >
                {tabs.map(tabInfo => {
                    const tabElement = (
                        <Tab
                            key={`${tabInfo.header.label}-header`}
                            data-cy={tabInfo.header['data-cy']}
                            label={tabInfo.header.label}
                            style={
                                tabInfo.header.error
                                    ? {
                                          color: syskronPalette.common.red
                                      }
                                    : undefined
                            }
                            {...tabInfo.header.additionalProps}
                        />
                    )

                    return tabInfo.header.additionalProps?.disabled ? (
                        // Need this trick to have a tooltip on disabled element
                        <Tooltip title={tabInfo.header.additionalProps?.tooltip || ''}>
                            <span>{tabElement}</span>
                        </Tooltip>
                    ) : (
                        tabElement
                    )
                })}
            </MuiTabs>
            {dataArea}
        </div>
    )
}
export const Tabs = observer(_Tabs)

export interface TabInfoWithId extends TabInfo {
    id: string
}

export interface QueryParamsTabsProps extends Omit<TabsProps, 'selectedTab' | 'setSelectedTab'> {
    queryParameterName: string
    defaultTabId?: string
    tabs: TabInfoWithId[]
}

function _QueryParamsTabs(props: QueryParamsTabsProps) {
    const {queryParameterName, defaultTabId, tabs, ...rest} = props
    const [selectedTabId, setSelectedTabId] = useQueryParameterState(queryParameterName, defaultTabId)

    const selectedTab = tabs.findIndex(x => x.id === selectedTabId)

    const setSelectedTab = React.useCallback(
        (newIndex: number) => {
            if (0 <= newIndex && newIndex < tabs.length) {
                setSelectedTabId(tabs[newIndex].id)
            } else {
                setSelectedTabId(null)
            }
        },
        [setSelectedTabId, tabs]
    )

    return <Tabs {...rest} tabs={tabs} selectedTab={selectedTab} setSelectedTab={setSelectedTab} />
}
export const QueryParamsTabs = observer(_QueryParamsTabs)

export default Tabs
